import React, { useState, useCallback, useEffect, useRef } from 'react';
import type { CachedEmail } from '../../stores/email-store';
import type { NormalizedEmail } from '@signa/shared/types';
import { PersonAvatar } from '../today/PersonAvatar';
import { SignaGlyph } from '../pa/SignaGlyph';
import { FocusHeader } from './FocusHeader';
import { FocusAction } from './FocusAction';
import { CatchupPane } from './CatchupPane';
import type { MemoryContext } from '../../lib/memory-api';
import { useAuthStore } from '../../stores/authStore';
import { useToastStore } from '../../stores/toast-store';
import { API_BASE } from '../../lib/api-config';
import { decodeHtmlEntities } from '../../lib/html-utils';
import { useEmailStore } from '../../stores/email-store';

/**
 * Email focus - two-pane content. Left: email body + actions + Draft.
 * Right: the shared CatchupPane fed with person context.
 */

interface EmailFocusProps {
  email: CachedEmail;
  fullEmail?: NormalizedEmail | null;
  context: MemoryContext | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  suggestedDraft?: string;
}

const NOREPLY_PATTERN = /noreply|no-reply|newsletter|notifications|billing|mailer|automated/i;

export function EmailFocus({
  email,
  fullEmail,
  context,
  loading,
  error,
  onClose,
  suggestedDraft,
}: EmailFocusProps) {
  const isNoreply = NOREPLY_PATTERN.test(email.fromEmail);
  const draftText = suggestedDraft ?? buildDefaultDraft(email);
  const showDraft = !isNoreply && !draftText.includes('[point 1]');


  const token = useAuthStore((s) => s.token);
  const addToast = useToastStore((s) => s.addToast);
  const archiveEmail = useEmailStore((s) => s.archiveEmail);
  const trashEmail = useEmailStore((s) => s.trashEmail);
  const [unsubState, setUnsubState] = useState<'idle' | 'loading' | 'done'>('idle');

  const openReplyComposer = useCallback((body: string) => {
    window.dispatchEvent(
      new CustomEvent('signa:compose', {
        detail: {
          to: email.fromEmail,
          subject: email.subject.toLowerCase().startsWith('re:') ? email.subject : `Re: ${email.subject}`,
          body,
        },
      })
    );
  }, [email.fromEmail, email.subject]);

  const handleArchive = useCallback(async () => {
    try {
      await archiveEmail(email.id);
      addToast({ message: 'Archived message', type: 'success' });
      onClose();
    } catch {
      addToast({ message: 'Could not archive message. Try again.', type: 'error' });
    }
  }, [archiveEmail, email.id, addToast, onClose]);

  const handleDelete = useCallback(async () => {
    try {
      await trashEmail(email.id);
      addToast({ message: 'Deleted message', type: 'success' });
      onClose();
    } catch {
      addToast({ message: 'Could not delete message. Try again.', type: 'error' });
    }
  }, [trashEmail, email.id, addToast, onClose]);

  const handleUnsubscribe = useCallback(async () => {
    if (unsubState !== 'idle') return;
    setUnsubState('loading');
    try {
      const res = await fetch(`${API_BASE}/emails/${email.id}/unsubscribe`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token ?? ''}` },
      });
      if (!res.ok) throw new Error('Unsubscribe failed');
      const data = await res.json() as { type: string; url?: string };
      if (data.type === 'url' && data.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      } else {
        addToast({ message: 'Unsubscribed successfully', type: 'success' });
      }
      setUnsubState('done');
    } catch {
      addToast({ message: 'Could not unsubscribe. Try again.', type: 'error' });
      setUnsubState('idle');
    }
  }, [email.id, token, addToast, unsubState]);

  return (
    <>
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        <FocusHeader onClose={onClose} />
        <div
          style={{
            padding: '20px 44px 80px',
            overflowY: 'auto',
            flex: 1,
          }}
        >
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 30,
              letterSpacing: '-0.5px',
              lineHeight: 1.2,
              color: 'var(--ink)',
              marginBottom: 22,
              fontWeight: 400,
              margin: '0 0 22px 0',
            }}
          >
            {decodeHtmlEntities(email.subject)}
          </h1>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginBottom: 28,
              paddingBottom: 20,
              borderBottom: '1px solid var(--rule)',
            }}
          >
            <PersonAvatar
              name={email.fromName || email.fromEmail}
              email={email.fromEmail}
              size={40}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: 'var(--ink)',
                }}
              >
                {email.fromName || email.fromEmail}
              </div>
              {email.fromName && email.fromEmail && (
                <div
                  style={{
                    fontSize: 12.5,
                    color: 'var(--ink-soft)',
                  }}
                >
                  {email.fromEmail}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  color: 'var(--ink-faint)',
                  letterSpacing: '0.5px',
                }}
              >
                {formatDateTime(email.receivedAt)}
              </span>
              {email.listUnsubscribe && (
                unsubState === 'done' ? (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      fontSize: 11.5,
                      color: 'var(--text-tertiary)',
                      fontFamily: 'var(--font-ui)',
                    }}
                  >
                    <UnsubscribedIcon />
                    Unsubscribed
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={handleUnsubscribe}
                    disabled={unsubState === 'loading'}
                    title="Unsubscribe from this mailing list"
                    style={{
                      padding: '3px 10px',
                      fontSize: 11.5,
                      fontFamily: 'var(--font-ui)',
                      color: 'var(--ink-soft)',
                      background: 'transparent',
                      border: '1px solid var(--rule)',
                      borderRadius: 5,
                      cursor: unsubState === 'loading' ? 'wait' : 'pointer',
                      opacity: unsubState === 'loading' ? 0.6 : 1,
                      transition: 'border-color 120ms, color 120ms',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'var(--error)';
                      e.currentTarget.style.color = 'var(--error)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--rule)';
                      e.currentTarget.style.color = 'var(--ink-soft)';
                    }}
                  >
                    {unsubState === 'loading' ? 'Unsubscribing...' : 'Unsubscribe'}
                  </button>
                )
              )}
            </div>
          </div>

          <MessageActions
            placement="before"
            isNoreply={isNoreply}
            onReply={() => openReplyComposer('')}
            onArchive={handleArchive}
            onDelete={handleDelete}
          />

          <EmailBody fullEmail={fullEmail} snippet={email.snippet} />

          <MessageActions
            placement="after"
            isNoreply={isNoreply}
            onReply={() => openReplyComposer('')}
            onArchive={handleArchive}
            onDelete={handleDelete}
          />

          {/* Draft I'd Send - hidden for noreply senders and placeholder drafts */}
          {showDraft && (
            <div
              style={{
                marginTop: 28,
                padding: '18px 20px',
                border: '1px dashed var(--rule)',
                borderRadius: 12,
                background: 'var(--briefing-bg)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 10,
                }}
              >
                <SignaGlyph size={14} />
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 10,
                    letterSpacing: '1.2px',
                    textTransform: 'uppercase',
                    color: 'var(--ink-faint)',
                  }}
                >
                  Draft I&rsquo;d send
                </span>
              </div>
              <div
                style={{
                  fontSize: 14,
                  color: 'var(--ink)',
                  lineHeight: 1.65,
                  textWrap: 'pretty',
                }}
              >
                &ldquo;{draftText}&rdquo;
              </div>
              <div style={{ marginTop: 12, display: 'flex', gap: 6 }}>
                <FocusAction small accent onClick={() => openReplyComposer(draftText)}>
                  Use this
                </FocusAction>
                <FocusAction small disabled>Edit</FocusAction>
                <FocusAction small disabled>Try another tone</FocusAction>
              </div>
            </div>
          )}
        </div>
      </div>

      <CatchupPane
        kind="email"
        context={context}
        loading={loading}
        error={error}
        personName={email.fromName || email.fromEmail}
        personEmail={email.fromEmail}
      />
    </>
  );
}

function MessageActions({
  placement,
  isNoreply,
  onReply,
  onArchive,
  onDelete,
}: {
  placement: 'before' | 'after';
  isNoreply: boolean;
  onReply: () => void;
  onArchive: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      role="group"
      aria-label={`Message actions ${placement} email`}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        marginBottom: placement === 'before' ? 28 : 0,
        marginTop: placement === 'after' ? 36 : 0,
      }}
    >
      <FocusAction accent onClick={onReply} disabled={isNoreply}>
        Reply
      </FocusAction>
      <FocusAction onClick={onArchive}>Archive</FocusAction>
      <FocusAction disabled>Snooze</FocusAction>
      <FocusAction disabled>Turn into todo</FocusAction>
      <FocusAction onClick={onDelete}>Delete</FocusAction>
    </div>
  );
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    now.getFullYear() === d.getFullYear() &&
    now.getMonth() === d.getMonth() &&
    now.getDate() === d.getDate();
  if (sameDay) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function buildDefaultDraft(email: CachedEmail): string {
  const firstName = (email.fromName ?? '').split(' ')[0] ?? 'there';
  return `${firstName}, quick turn around. I've read the thread and have two things to flag: [point 1] and [point 2]. I'll get you a proper response by end of day.`;
}

function UnsubscribedIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
      focusable="false"
      style={{ color: 'var(--success)', flexShrink: 0 }}
    >
      <path
        d="M2.25 6.25L4.8 8.65L9.75 3.35"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Email body renderer

type EmailBodyTheme = {
  text: string;
  accent: string;
  background: string;
  mode: 'dark' | 'light';
};

function safeCssColor(value: string): string {
  return value.replace(/[<>"{};]/g, '').trim();
}

function getEmailBodyTheme(): EmailBodyTheme {
  if (typeof window === 'undefined') {
    return {
      text: 'CanvasText',
      accent: 'LinkText',
      background: 'Canvas',
      mode: 'dark',
    };
  }

  const styles = window.getComputedStyle(document.documentElement);
  const mode = document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
  const text =
    safeCssColor(styles.getPropertyValue('--text-primary')) ||
    safeCssColor(styles.getPropertyValue('--ink')) ||
    'CanvasText';
  const accent =
    safeCssColor(styles.getPropertyValue('--accent')) ||
    safeCssColor(styles.getPropertyValue('--link')) ||
    'LinkText';
  const background =
    safeCssColor(styles.getPropertyValue('--paper')) ||
    safeCssColor(styles.getPropertyValue('--app-bg')) ||
    'Canvas';

  return { text, accent, background, mode };
}

function useEmailBodyTheme(): EmailBodyTheme {
  const [theme, setTheme] = useState<EmailBodyTheme>(() => getEmailBodyTheme());

  useEffect(() => {
    if (typeof MutationObserver === 'undefined') return;

    const root = document.documentElement;
    const syncTheme = () => {
      const next = getEmailBodyTheme();
      setTheme((current) =>
        current.mode === next.mode &&
        current.text === next.text &&
        current.accent === next.accent &&
        current.background === next.background
          ? current
          : next
      );
    };
    const observer = new MutationObserver(syncTheme);
    observer.observe(root, {
      attributes: true,
      attributeFilter: ['data-theme', 'data-accent', 'style'],
    });
    syncTheme();
    return () => observer.disconnect();
  }, []);

  return theme;
}

const EMAIL_SURFACE_ELEMENTS =
  'table,thead,tbody,tfoot,tr,td,th,div,section,article,header,footer,main,p,span';

const HTML_WRAPPER = (body: string, theme: EmailBodyTheme) => {
  const darkOverrides =
    theme.mode === 'dark'
      ? `<style id="signa-email-dark-mode">` +
        `body#signa-email-body#signa-email-body{background-color:var(--email-body-bg)!important;` +
        `color:var(--email-body-text)!important;}` +
        `body#signa-email-body#signa-email-body :where(${EMAIL_SURFACE_ELEMENTS}){` +
        `border-color:rgba(244,244,251,.14)!important;}` +
        `body#signa-email-body#signa-email-body :where(` +
        `[bgcolor="#fff" i],[bgcolor="#ffffff" i],[style*="background:#fff" i],` +
        `[style*="background: #fff" i],[style*="background-color:#fff" i],` +
        `[style*="background-color: #fff" i]){` +
        `background-color:transparent!important;color:var(--email-body-text)!important;}` +
        `body#signa-email-body#signa-email-body a:not([style*="background" i]){` +
        `color:var(--email-body-accent)!important;}` +
        `</style>`
      : '';

  return (
    `<!DOCTYPE html><html data-email-theme="${theme.mode}"><head>` +
    `<base target="_blank">` +
    `<meta charset="utf-8">` +
    `<style>` +
    `:root{color-scheme:${theme.mode};--email-body-text:${theme.text};` +
    `--email-body-accent:${theme.accent};--email-body-bg:${theme.background};}` +
    `html,body{margin:0;padding:8px 0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;` +
    `font-size:14px;line-height:1.6;color:var(--email-body-text);word-break:break-word;` +
    `background-color:var(--email-body-bg)!important;}` +
    `img{max-width:100%;height:auto;}a{color:var(--email-body-accent);}` +
    `table{max-width:100%;}` +
    `</style></head><body id="signa-email-body">${body}${darkOverrides}</body></html>`
  );
};

function parseRgb(color: string): [number, number, number] | null {
  const match = color.match(/rgba?\(\s*(\d+)\D+(\d+)\D+(\d+)/i);
  if (!match) return null;
  const red = Number(match[1]);
  const green = Number(match[2]);
  const blue = Number(match[3]);
  if (![red, green, blue].every(Number.isFinite)) return null;
  return [red, green, blue];
}

function isNeutralColor([red, green, blue]: [number, number, number]): boolean {
  return Math.max(red, green, blue) - Math.min(red, green, blue) <= 28;
}

function enforceDarkEmailSurface(doc: Document, theme: EmailBodyTheme): void {
  if (theme.mode !== 'dark') return;

  doc.documentElement.style.setProperty('background-color', theme.background, 'important');
  doc.body.style.setProperty('background-color', theme.background, 'important');
  doc.body.style.setProperty('color', theme.text, 'important');

  const view = doc.defaultView;
  if (!view) return;
  doc.querySelectorAll<HTMLElement>(EMAIL_SURFACE_ELEMENTS).forEach((element) => {
    if (element.closest('a')) return;
    const styles = view.getComputedStyle(element);
    const background = parseRgb(styles.backgroundColor);
    const color = parseRgb(styles.color);

    if (background && isNeutralColor(background) && Math.min(...background) >= 218) {
      element.style.setProperty('background-color', 'transparent', 'important');
    }
    if (color && isNeutralColor(color) && Math.max(...color) <= 96) {
      element.style.setProperty('color', theme.text, 'important');
    }
  });
}

function EmailBody({
  fullEmail,
  snippet,
}: {
  fullEmail?: NormalizedEmail | null;
  snippet: string;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState(400);
  const theme = useEmailBodyTheme();

  if (fullEmail?.bodyHtml) {
    return (
      <iframe
        ref={iframeRef}
        srcDoc={HTML_WRAPPER(fullEmail.bodyHtml, theme)}
        sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
        title="Email body"
        style={{
          width: '100%',
          border: 'none',
          display: 'block',
          height: iframeHeight,
          minHeight: 400,
          background: 'var(--paper)',
          colorScheme: theme.mode,
        }}
        onLoad={() => {
          const doc = iframeRef.current?.contentDocument;
          if (!doc) return;
          enforceDarkEmailSurface(doc, theme);
          const h = doc.documentElement.scrollHeight;
          if (h > 0) setIframeHeight(h + 16);
        }}
      />
    );
  }

  if (fullEmail?.bodyText) {
    return (
      <pre
        style={{
          fontSize: 14,
          lineHeight: 1.65,
          color: 'var(--ink)',
          margin: 0,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontFamily: 'inherit',
        }}
      >
        {fullEmail.bodyText}
      </pre>
    );
  }

  // Snippet fallback while full email is loading (or unavailable)
  const paragraphs = snippet
    .trim()
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);

  if (paragraphs.length === 0) {
    return (
      <p style={{ fontSize: 15.5, color: 'var(--ink-faint)', fontStyle: 'italic', margin: 0 }}>
        No body content available.
      </p>
    );
  }
  return (
    <>
      {paragraphs.map((p, i) => (
        <p
          key={i}
          style={{ fontSize: 15.5, lineHeight: 1.65, color: 'var(--ink)', margin: '0 0 14px' }}
        >
          {decodeHtmlEntities(p)}
        </p>
      ))}
    </>
  );
}
